import { createSelector } from '@reduxjs/toolkit';
import { useRef } from 'preact/hooks';

import type { TemplateFunction, TemplateFunctionInvoker, TemplateResult } from 'Components/types';
import { setNextPage } from 'Core/actions/request';
import { useDropdown } from 'Core/hooks/index';
import {
  allItemsReceivedSelector,
  lastPageNumberSelector,
  noItemsReceivedSelector,
  searchRequestSelector,
} from 'Core/selectors/search';
import { useDispatch, useSelector } from 'Core/store';
import { getUriFromRequest } from 'Modules/converter/index';
import { cloneSafe } from 'Utils/components';

import { type PaginationButtonsTemplateParams } from './pagination';
import PaginationButton, { type PaginationButtonTemplateParams } from './paginationButton';
import PaginationDropdown, { type PaginationDropdownTemplateParams } from './paginationDropdown';
import { makeButtonProps, pageProps } from './propsHelper';

const hidePaginationSelector = createSelector(
  noItemsReceivedSelector,
  allItemsReceivedSelector,
  (noItemsReceived, allItemsReceived) => noItemsReceived || allItemsReceived,
);

const dropdownName = 'paginationDropdown';

interface Props {
  template: TemplateFunction<PaginationMenuTemplateParams>;
}

export interface PaginationMenuTemplateParams extends PaginationButtonsTemplateParams {
  hasNextPage: boolean;
  loadNextPage: () => void;
  paginationDropdown: TemplateFunctionInvoker<PaginationDropdownTemplateParams>;
}

export default function PaginationMenu({ template }: Props): TemplateResult {
  const dispatch = useDispatch();
  const hidePagination = useSelector(hidePaginationSelector);
  const pageNumber = useSelector((state) => state.search.request.pageNumber);
  const lastPageNumber = useSelector(lastPageNumberSelector);

  const searchRequest = useSelector(searchRequestSelector);
  const searchParams = getUriFromRequest(searchRequest).search;

  const rootRef = useRef<HTMLElement>(null);

  const { hideDropdown } = useDropdown(rootRef, '.cm_paginationMenu', {
    dropdownName,
    toggle: true,
  });

  if (hidePagination) {
    return null;
  }

  const buttons = makeButtonProps(pageNumber, lastPageNumber, true).map(
    (buttonProps) => (templ: TemplateFunction<PaginationButtonTemplateParams>) => {
      const props = {
        ...buttonProps,
        searchParams,
        template: templ,
        isActive: buttonProps.pageNumber === pageNumber,
        hideDropdown,
      };
      return (
        <PaginationButton {...props} key={`page-btn-${buttonProps.type}-${buttonProps.pageNumber ?? 0}`} />
      );
    },
  );

  const pages = Array.apply(0, Array(lastPageNumber + 1))
    .map((v, k) => pageProps(k, lastPageNumber))
    .map((buttonProps) => ({
      ...buttonProps,
      searchParams,
      isActive: buttonProps.pageNumber === pageNumber,
      hideDropdown,
    }));

  const dropdown = (templ: TemplateFunction<PaginationDropdownTemplateParams>) => (
    <PaginationDropdown template={templ} pages={pages} key="dropdown" />
  );

  const component = template.call({
    buttons,
    hasNextPage: lastPageNumber > pageNumber,
    loadNextPage: () => dispatch(setNextPage()),
    [dropdownName]: dropdown,
  });

  return cloneSafe(component, rootRef);
}
